import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store for UI settings and state
 */
interface UIState {
  // Theme
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  
  // Modal visibility control
  activeModal: string | null;
  openModal: (modalName: string) => void;
  closeModal: () => void;
  
  // Sidebar state (for mobile)
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  
  // Notifications
  notifications: Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    duration?: number;
  }>;
  addNotification: (notification: Omit<UIState['notifications'][0], 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),
      
      // Modal control
      activeModal: null,
      openModal: (modalName) => set({ activeModal: modalName }),
      closeModal: () => set({ activeModal: null }),
      
      // Sidebar state
      sidebarOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (isOpen) => set({ sidebarOpen: isOpen }),
      
      // Notifications
      notifications: [],
      addNotification: (notification) => {
        const id = `notification-${Date.now()}`;
        set((state) => ({
          notifications: [...state.notifications, { ...notification, id }]
        }));
        
        // Auto-remove notifications after their duration (default: 5 seconds)
        const duration = notification.duration || 5000;
        setTimeout(() => {
          get().removeNotification(id);
        }, duration);
        
        return id;
      },
      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id)
        }));
      },
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'custody-planner-ui-storage',
      // Only persist theme and sidebar state, not notifications or modals
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
);